import React, { Fragment, useState } from 'react';

import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import CircleQuestion from 'components/application/CircleQuestion';
import Tooltip from 'components/application/Tooltip';

import FieldRow from 'components/settings/personnel/information/FieldRow';

export default function FieldTable({
  formattedRows,
  onDestroyFieldAttribute,
  onDestroyFieldValues,
  onEdit,
  onIncludedToggle,
  onRestrictedToggle,
  onSortEnd,
}) {
  const sensors = useSensors(useSensor(PointerSensor));

  const [isSorting, setIsSorting] = useState(false);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'settings.personnel.information.FieldTable',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const orderedIds = formattedRows.map(
    (formattedRow) => formattedRow.fieldSetting.id,
  );

  function handleDragEnd(event) {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = orderedIds.indexOf(active.id);
      const newIndex = orderedIds.indexOf(over.id);
      onSortEnd({ oldIndex, newIndex });
    }

    setIsSorting(false);
  }

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToParentElement, restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
      onDragStart={() => setIsSorting(true)}
      sensors={sensors}
    >
      <div className='no-user-select tw-relative tw-z-[1]'>
        <div className='header-bar header-bar--border tw-border-grey-100 tw-bg-grey-050 tw-px-5'>
          <div className='tw-w-13 tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'>
            {i18nTCommon('shared.include')}
          </div>
          <div className='tw-min-w-0 tw-flex-1 tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'>
            {i18nTCommon('shared.name')}
          </div>
          <div className='tw-w-37 tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'>
            {i18nTCommon('personnel.fieldType')}
          </div>
          <div className='tw-w-30 tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'>
            <div className='tw-flex tw-items-center'>
              <span className='tw-mr-1'>
                {i18nTCommon('shared.visibility')}
              </span>
              <Tooltip
                placement='top'
                tooltip={i18nT('visibility.tooltip')}
                trigger='hover'
              >
                <CircleQuestion size='sm' />
              </Tooltip>
            </div>
          </div>
          <div className='tw-w-22 tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'>
            <div className='tw-flex tw-items-center'>
              <span className='tw-mr-1'>{i18nT('records.header')}</span>
              <Tooltip
                placement='top'
                tooltip={i18nT('records.tooltip')}
                trigger='hover'
              >
                <CircleQuestion size='sm' />
              </Tooltip>
            </div>
          </div>
          <div className='tw-w-12 tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'>
            {i18nTCommon('shared.actions')}
          </div>
        </div>
        <SortableContext
          items={orderedIds}
          strategy={verticalListSortingStrategy}
        >
          <ul className='block-list block-list--border-bottom-none tw-relative tw-z-[1]'>
            {formattedRows.map((formattedRow) => (
              <Fragment key={formattedRow.fieldSetting.id}>
                {isSorting && (
                  <div className='row-bar--ghost tw-border-grey-100' />
                )}
                <FieldRow
                  fieldAttribute={formattedRow.fieldAttribute}
                  fieldSetting={formattedRow.fieldSetting}
                  key={formattedRow.fieldSetting.id}
                  onDestroyFieldAttribute={onDestroyFieldAttribute}
                  onDestroyFieldValues={onDestroyFieldValues}
                  onEdit={onEdit}
                  onIncludedToggle={onIncludedToggle}
                  onRestrictedToggle={onRestrictedToggle}
                />
              </Fragment>
            ))}
          </ul>
        </SortableContext>
      </div>
    </DndContext>
  );
}

FieldTable.propTypes = {
  formattedRows: PropTypes.arrayOf(
    PropTypes.shape({
      fieldAttribute: PropTypes.object.isRequired,
      fieldSetting: PropTypes.object.isRequired,
    }),
  ).isRequired,
  onIncludedToggle: PropTypes.func.isRequired,
  onRestrictedToggle: PropTypes.func.isRequired,
  onSortEnd: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDestroyFieldAttribute: PropTypes.func.isRequired,
  onDestroyFieldValues: PropTypes.func.isRequired,
};
